import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Prompt } from '../components/Prompt';
import { TerminalWindow } from '../components/TerminalWindow';
import { cx } from '../lib/cx';
import { usePrefersReducedMotion } from '../lib/usePrefersReducedMotion';
import styles from './Hero.module.css';
import { services } from '../data/services';

type Line =
  | { kind: 'cmd'; text: string }
  | { kind: 'out'; node: ReactNode };

const LINES: readonly Line[] = [
  { kind: 'cmd', text: 'whoami' },
  { kind: 'out', node: <div className={styles.out}>SeaxDev, software development studio</div> },
  { kind: 'cmd', text: 'ls services/' },
  {
    kind: 'out',
    node: (
      <div className={styles.dir}>
        {services.map((s) => (
          <span key={s.name}>{s.name}</span>
        ))}
      </div>
    ),
  },
  { kind: 'cmd', text: 'systemctl status your-project' },
  {
    kind: 'out',
    node: (
      <div className={styles.out}>
        <span className={styles.ok}>●</span> your-project.service
      </div>
    ),
  },
  {
    kind: 'out',
    node: (
      <div className={styles.out}>
        &nbsp;&nbsp;&nbsp;Active: <span className={styles.ok}>active (running)</span>
      </div>
    ),
  },
];

/** Type each command out on load; output lines appear whole. Skipped under reduced motion. */
function useTypewriter(lines: readonly Line[], animate: boolean) {
  const steps = useMemo(() => lines.map((l) => (l.kind === 'cmd' ? l.text.length : 1)), [lines]);
  const total = useMemo(() => steps.reduce((a, b) => a + b, 0), [steps]);
  const [step, setStep] = useState(() => (animate ? 0 : total));

  useEffect(() => {
    if (!animate) {
      setStep(total);
      return;
    }
    if (step >= total) return;

    // Find the line the current step falls in to pick a delay.
    let acc = 0;
    let i = 0;
    while (i < lines.length && acc + steps[i] <= step) {
      acc += steps[i];
      i++;
    }
    const line = lines[i];
    const startOfLine = step === acc;
    const delay = line.kind === 'cmd' ? (startOfLine ? 550 : 45) : 200;

    const t = window.setTimeout(() => setStep((s) => s + 1), delay);
    return () => window.clearTimeout(t);
  }, [animate, lines, step, steps, total]);

  // Per-line visibility: how many chars of a cmd are shown; outputs are all-or-nothing.
  let acc = 0;
  const shown = lines.map((_, i) => {
    const visible = Math.max(0, Math.min(steps[i], step - acc));
    acc += steps[i];
    return visible;
  });

  return { shown, done: step >= total };
}

export function Hero() {
  const reduced = usePrefersReducedMotion();
  const { shown, done } = useTypewriter(LINES, !reduced);

  return (
    <section className={styles.hero}>
      <div className={styles.copy}>
        <div className={styles.kicker}># IT software development</div>
        <h1 className={styles.title}>
          Software built like a well-kept server: <em>stable, fast, yours.</em>
        </h1>
        <p className={styles.lead}>
          We design, build and maintain web apps, mobile apps and backend systems for businesses
          that need software that just runs.
        </p>
        <div className={styles.ctas}>
          <a href="#contact" className="btn btn--primary">
            $ start a project
          </a>
          <a href="#services" className="btn btn--outline">
            ls ./services
          </a>
        </div>
      </div>

      <TerminalWindow title="seax@dev: ~" className={styles.term} bodyClassName={styles.body}>
        {LINES.map((line, i) => {
          const visible = shown[i];
          if (visible === 0) return null;
          if (line.kind === 'out') return <div key={i}>{line.node}</div>;
          const typing = visible < line.text.length;
          return (
            <Prompt key={i} className={cx(styles.cmd, i === 0 && styles.first)}>
              {line.text.slice(0, visible)}
              {typing && <span className={styles.cursor} aria-hidden="true" />}
            </Prompt>
          );
        })}
        {done && (
          <Prompt className={styles.cmd}>
            <span className={styles.cursor} aria-hidden="true" />
          </Prompt>
        )}
      </TerminalWindow>
    </section>
  );
}
